import type { ReactNode } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useTenant } from "../../hooks/useTenant";
import { getRoleLabel, ROLE_REDIRECTS } from "../../lib/constants";
import { Button } from "../ui/Button";

export function AppShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const { currentRole, currentTenant } = useTenant();
  const homePath = currentRole ? ROLE_REDIRECTS[currentRole] : "/";
  const adminLinks = [
    ["/admin", "Overview"],
    ["/admin/tenants", "Tenants"],
    ["/admin/users", "Users"],
    ["/admin/invites", "Invites"],
    ["/admin/metrics", "Metrics"],
    ["/admin/data-health", "Data health"],
    ["/admin/audit-logs", "Audit logs"],
    ["/admin/feature-flags", "Feature flags"],
    ["/admin/settings", "Settings"],
  ] as const;
  const trainerLinks = [
    ["/trainer", "Overview"],
    ["/trainer/clients", "Clients"],
    ["/trainer/workouts", "Workouts"],
    ["/trainer/meals", "Meals"],
    ["/trainer/glossary", "Glossary"],
    ["/trainer/exercises", "Exercises"],
    ["/trainer/team", "Team"],
    ["/trainer/profile", "Profile"],
    ["/trainer/business", "Business"],
    ["/trainer/settings", "Settings"],
  ] as const;
  const clientLinks = [
    ["/client", "Overview"],
    ["/client/workouts", "Workouts"],
    ["/client/meals", "Meals"],
    ["/client/glossary", "Glossary"],
    ["/client/onboarding", "Onboarding"],
  ] as const;
  const navLinks = currentRole === "platform_admin"
    ? adminLinks
    : currentRole === "tenant_owner" || currentRole === "trainer" || currentRole === "assistant"
      ? trainerLinks
      : currentRole === "client"
        ? clientLinks
        : [[homePath, "Overview"] as const];

  async function handleSignOut() {
    await signOut();
    navigate("/login", { replace: true });
  }

  return (
    <div className="min-h-screen bg-paper text-ink">
      <div className="flex min-h-screen">
        <aside className="hidden w-72 border-r border-line bg-field px-5 py-6 lg:block">
          <div className="flex items-center gap-3">
            <div className="grid-mark grid-mark-dark" aria-hidden="true">
              <span />
              <span />
              <span />
              <span />
            </div>
            <div>
              <p className="text-base font-semibold">CoachGrid</p>
              <p className="text-xs text-graphite/60">
                {currentTenant?.name ?? "Workspace"}
              </p>
            </div>
          </div>

          <nav className="mt-10 space-y-1">
            {navLinks.map(([to, label]) => (
              <NavLink
                className={({ isActive }) =>
                  [
                    "block rounded-md px-3 py-2.5 text-sm font-medium transition",
                    isActive
                      ? "bg-ink text-white"
                      : "text-graphite hover:bg-ink/5 hover:text-ink",
                  ].join(" ")
                }
                end={to === "/admin"}
                key={to}
                to={to}
              >
                {label}
              </NavLink>
            ))}
          </nav>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="border-b border-line bg-paper/95 px-5 py-4 backdrop-blur sm:px-8">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-graphite/70">
                  {getRoleLabel(currentRole)}
                </p>
                <p className="mt-1 text-sm text-graphite/55">
                  {profile?.email ?? "Signed in"}
                </p>
              </div>
              <Button onClick={() => void handleSignOut()} variant="secondary">
                Sign out
              </Button>
            </div>
          </header>

          <main className="flex-1 px-5 py-8 sm:px-8 lg:px-10">{children}</main>
        </div>
      </div>
    </div>
  );
}
